import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Wrench, Plus, Search, Edit, Trash2, Calendar, AlertTriangle,
  CheckCircle, Clock, XCircle, DollarSign, User, Package, Filter, History
} from 'lucide-react';

const TYPES = [
  { value: 'preventivo', label: 'Preventivo', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'correctivo', label: 'Correctivo', color: 'bg-orange-500/20 text-orange-400' },
  { value: 'calibracion', label: 'Calibración', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'inspeccion', label: 'Inspección', color: 'bg-cyan-500/20 text-cyan-400' },
  { value: 'limpieza', label: 'Limpieza', color: 'bg-emerald-500/20 text-emerald-400' },
  { value: 'actualizacion', label: 'Actualización / Upgrade', color: 'bg-indigo-500/20 text-indigo-400' },
  { value: 'reparacion', label: 'Reparación', color: 'bg-rose-500/20 text-rose-400' },
];

const STATUSES = [
  { value: 'programado', label: 'Programado', icon: Clock, color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'aprobado', label: 'Aprobado', icon: CheckCircle, color: 'bg-teal-500/20 text-teal-400' },
  { value: 'en_progreso', label: 'En Progreso', icon: Wrench, color: 'bg-blue-500/20 text-blue-400' },
  { value: 'en_espera_piezas', label: 'Esperando Piezas', icon: Clock, color: 'bg-amber-500/20 text-amber-400' },
  { value: 'en_revision', label: 'En Revisión', icon: AlertTriangle, color: 'bg-purple-500/20 text-purple-400' },
  { value: 'completado', label: 'Completado', icon: CheckCircle, color: 'bg-green-500/20 text-green-400' },
  { value: 'cancelado', label: 'Cancelado', icon: XCircle, color: 'bg-red-500/20 text-red-400' },
  { value: 'rechazado', label: 'Rechazado', icon: XCircle, color: 'bg-rose-500/20 text-rose-400' },
];

const emptyForm = {
  equipment_id: '', unit_id: '', equipment_name: '', unit_serial: '',
  maintenance_type: 'preventivo', status: 'programado', priority: 'normal',
  description: '', technician: '', cost: 0, scheduled_date: '', start_date: '',
  completion_date: '', next_maintenance_date: '', notes: '', parts_replaced: []
};

const PRIORITIES = [
  { value: 'baja', label: 'Baja', color: 'bg-slate-500/20 text-slate-400' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'alta', label: 'Alta', color: 'bg-orange-500/20 text-orange-400' },
  { value: 'urgente', label: 'Urgente', color: 'bg-red-500/20 text-red-400' },
];

const MaintenancePage = () => {
  const { api } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [equipment, setEquipment] = useState([]);
  const [units, setUnits] = useState([]);
  const [alerts, setAlerts] = useState({ overdue: [], upcoming: [], total_overdue: 0, total_upcoming: 0 });
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [recRes, eqRes, alertRes] = await Promise.all([
        api().get('/maintenance'),
        api().get('/equipment'),
        api().get('/maintenance/upcoming/alerts')
      ]);
      setRecords(recRes.data);
      setEquipment(eqRes.data || []);
      setAlerts(alertRes.data);
    } catch (err) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadUnits = async (eqId) => {
    try {
      const res = await api().get(`/equipment/${eqId}/units`);
      setUnits(res.data || []);
    } catch { setUnits([]); }
  };

  const handleEquipmentChange = (eqId) => {
    const eq = equipment.find(e => e.id === eqId);
    setForm(prev => ({ ...prev, equipment_id: eqId, equipment_name: eq?.name || '', unit_id: '', unit_serial: '' }));
    loadUnits(eqId);
  };

  const handleUnitChange = (unitId) => {
    const u = units.find(un => un.id === unitId);
    setForm(prev => ({ ...prev, unit_id: unitId, unit_serial: u?.serial_number || '' }));
  };

  const handleSave = async () => {
    if (!form.equipment_id) { toast.error('Selecciona un equipo'); return; }
    if (!form.description) { toast.error('Agrega una descripción'); return; }
    try {
      if (editingId) {
        await api().put(`/maintenance/${editingId}`, form);
        toast.success('Registro actualizado');
      } else {
        await api().post('/maintenance', form);
        toast.success('Mantenimiento registrado');
      }
      setShowModal(false);
      setEditingId(null);
      setForm(emptyForm);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (record) => {
    setForm(record);
    setEditingId(record.id);
    if (record.equipment_id) loadUnits(record.equipment_id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try {
      await api().delete(`/maintenance/${id}`);
      toast.success('Registro eliminado');
      loadData();
    } catch { toast.error('Error al eliminar'); }
  };

  const openTimeline = async (equipmentId) => {
    setTimelineLoading(true);
    setShowTimeline(true);
    try {
      const res = await api().get(`/maintenance/timeline/${equipmentId}`);
      setTimelineData(res.data);
    } catch {
      toast.error('Error cargando timeline');
      setShowTimeline(false);
    } finally {
      setTimelineLoading(false);
    }
  };

  const filtered = records.filter(r => {
    const matchSearch = !search || r.equipment_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.technician?.toLowerCase().includes(search.toLowerCase()) || r.description?.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'all' || r.maintenance_type === filterType;
    const matchStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchSearch && matchType && matchStatus;
  });

  const getTypeBadge = (type) => { const t = TYPES.find(x => x.value === type); return t ? <Badge className={t.color}>{t.label}</Badge> : <Badge>{type}</Badge>; };
  const getStatusBadge = (status) => { const s = STATUSES.find(x => x.value === status); return s ? <Badge className={s.color}>{s.label}</Badge> : <Badge>{status}</Badge>; };
  const getPriorityBadge = (priority) => { const p = PRIORITIES.find(x => x.value === priority); return p ? <Badge className={p.color}>{p.label}</Badge> : null; };

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Wrench className="h-6 w-6 text-primary" />Mantenimiento</h1>
          <p className="text-sm text-muted-foreground">Historial y programación de mantenimientos</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setEditingId(null); setShowModal(true); }} data-testid="new-maintenance-btn">
          <Plus className="h-4 w-4 mr-2" />Nuevo Mantenimiento
        </Button>
      </div>

      {/* Alerts */}
      {(alerts.total_overdue > 0 || alerts.total_upcoming > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {alerts.total_overdue > 0 && (
            <Card className="border-red-500/30 bg-red-500/5">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="h-8 w-8 text-red-400" />
                <div><p className="font-bold text-red-400">{alerts.total_overdue} Vencidos</p><p className="text-xs text-muted-foreground">Mantenimientos con fecha pasada</p></div>
              </CardContent>
            </Card>
          )}
          {alerts.total_upcoming > 0 && (
            <Card className="border-yellow-500/30 bg-yellow-500/5">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="h-8 w-8 text-yellow-400" />
                <div><p className="font-bold text-yellow-400">{alerts.total_upcoming} Próximos</p><p className="text-xs text-muted-foreground">Esta semana</p></div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="maintenance-search" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground flex items-center">{filtered.length} registro(s)</div>
      </div>

      {/* Records List */}
      {loading ? (
        <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="text-center py-12 text-muted-foreground"><Wrench className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No hay registros de mantenimiento</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => {
            const isOverdue = r.status !== 'completado' && r.status !== 'cancelado' && r.scheduled_date && new Date(r.scheduled_date) < new Date();
            return (
            <Card key={r.id} className={`hover:border-primary/30 transition-colors ${isOverdue ? 'border-red-500/50 bg-red-500/5' : ''}`} data-testid={`maintenance-record-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium truncate">{r.equipment_name}</span>
                      {r.unit_serial && <span className="text-xs text-muted-foreground">S/N: {r.unit_serial}</span>}
                      {getTypeBadge(r.maintenance_type)}
                      {getStatusBadge(r.status)}
                      {r.priority && r.priority !== 'normal' && getPriorityBadge(r.priority)}
                      {isOverdue && <Badge className="bg-red-600 text-white animate-pulse text-xs">VENCIDO</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1">{r.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                      {r.technician && <span className="flex items-center gap-1"><User className="h-3 w-3" />{r.technician}</span>}
                      {r.scheduled_date && <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-500 font-semibold' : ''}`}><Calendar className="h-3 w-3" />{new Date(r.scheduled_date).toLocaleDateString()}{isOverdue && ` (${Math.ceil((new Date() - new Date(r.scheduled_date)) / (1000*60*60*24))} dias vencido)`}</span>}
                      {r.cost > 0 && <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />${r.cost} {r.currency}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openTimeline(r.equipment_id)} title="Ver Timeline" data-testid={`timeline-${r.id}`}><History className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(r)} data-testid={`edit-maintenance-${r.id}`}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(r.id)} data-testid={`delete-maintenance-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wrench className="h-5 w-5 text-primary" />{editingId ? 'Editar' : 'Nuevo'} Mantenimiento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Equipo *</Label>
                <Select value={form.equipment_id} onValueChange={handleEquipmentChange}>
                  <SelectTrigger data-testid="maint-equipment-select"><SelectValue placeholder="Seleccionar equipo" /></SelectTrigger>
                  <SelectContent>{equipment.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unidad (opcional)</Label>
                <Select value={form.unit_id || "none"} onValueChange={(v) => handleUnitChange(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="maint-unit-select"><SelectValue placeholder="Seleccionar unidad" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {units.map(u => <SelectItem key={u.id} value={u.id}>{u.serial_number || u.id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.maintenance_type} onValueChange={v => setForm(prev => ({ ...prev, maintenance_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={form.status} onValueChange={v => setForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select value={form.priority || 'normal'} onValueChange={v => setForm(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Descripción del mantenimiento..." data-testid="maint-description" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Técnico</Label>
                <Input value={form.technician} onChange={e => setForm(prev => ({ ...prev, technician: e.target.value }))} placeholder="Nombre del técnico" />
              </div>
              <div className="space-y-2">
                <Label>Costo</Label>
                <Input type="number" value={form.cost} onChange={e => setForm(prev => ({ ...prev, cost: parseFloat(e.target.value) || 0 }))} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha Programada</Label>
                <Input type="date" value={form.scheduled_date?.split('T')[0] || ''} onChange={e => setForm(prev => ({ ...prev, scheduled_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))} />
              </div>
              <div className="space-y-2">
                <Label>Fecha de Inicio</Label>
                <Input type="date" value={form.start_date?.split('T')[0] || ''} onChange={e => setForm(prev => ({ ...prev, start_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha de Completación</Label>
                <Input type="date" value={form.completion_date?.split('T')[0] || ''} onChange={e => setForm(prev => ({ ...prev, completion_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))} />
              </div>
              <div className="space-y-2">
                <Label>Próximo Mantenimiento</Label>
                <Input type="date" value={form.next_maintenance_date?.split('T')[0] || ''} onChange={e => setForm(prev => ({ ...prev, next_maintenance_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas adicionales</Label>
              <Textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Notas..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={handleSave} data-testid="save-maintenance-btn">{editingId ? 'Actualizar' : 'Crear'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Modal */}
      <Dialog open={showTimeline} onOpenChange={setShowTimeline}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Timeline: {timelineData?.equipment_name || '...'}
            </DialogTitle>
          </DialogHeader>
          {timelineLoading ? (
            <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" /></div>
          ) : timelineData?.timeline?.length > 0 ? (
            <div className="relative pl-8 space-y-0" data-testid="maintenance-timeline">
              {/* Vertical line */}
              <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-border" />
              {timelineData.timeline.map((item, idx) => {
                const isCompleted = item.status === 'completado';
                const isCancelled = item.status === 'cancelado' || item.status === 'rechazado';
                const isOverdue = !isCompleted && !isCancelled && item.scheduled_date && new Date(item.scheduled_date) < new Date();
                const dotColor = isCompleted ? 'bg-emerald-500' : isCancelled ? 'bg-red-500' : isOverdue ? 'bg-amber-500' : 'bg-blue-500';
                
                return (
                  <div key={item.id || idx} className="relative pb-6 last:pb-0">
                    {/* Dot */}
                    <div className={`absolute -left-5 top-1 w-3 h-3 rounded-full ${dotColor} ring-2 ring-background`} />
                    <Card className={`${isOverdue ? 'border-amber-500/30' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {getTypeBadge(item.type)}
                          {getStatusBadge(item.status)}
                          {item.priority && item.priority !== 'normal' && getPriorityBadge(item.priority)}
                          {isOverdue && <Badge className="bg-amber-500/20 text-amber-400">Vencido</Badge>}
                        </div>
                        <p className="text-sm">{item.description || 'Sin descripcion'}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                          {item.scheduled_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Programado: {item.scheduled_date.slice(0,10)}</span>}
                          {item.start_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Inicio: {item.start_date.slice(0,10)}</span>}
                          {item.completion_date && <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-400" />Completado: {item.completion_date.slice(0,10)}</span>}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
                          {item.technician && <span className="flex items-center gap-1"><User className="h-3 w-3" />{item.technician}</span>}
                          {item.cost > 0 && <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />${item.cost}</span>}
                          {item.parts_replaced?.length > 0 && <span>Piezas: {item.parts_replaced.join(', ')}</span>}
                        </div>
                        {item.notes && <p className="text-xs text-muted-foreground mt-2 italic">{item.notes}</p>}
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Wrench className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No hay registros de mantenimiento para este equipo</p>
            </div>
          )}
          <DialogFooter>
            <div className="text-xs text-muted-foreground">
              {timelineData?.total_records || 0} registro(s) en total
            </div>
            <Button variant="outline" onClick={() => setShowTimeline(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaintenancePage;
